import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  Input,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Store } from '@ngxs/store';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import {
  GridComponent,
  RowArgs,
  RowSelectedFn,
  SelectionEvent
} from '@progress/kendo-angular-grid';
import { Observable, Subject } from 'rxjs';
import { filter, startWith, take, takeUntil } from 'rxjs/operators';
import { ClaimColumn } from '@module-claims/enums/claim-column.enum';
import {
  ClaimCEContact,
  ClaimsCEsContactParams,
  ClaimsCEsGetResponse
} from '@module-claims/models/claim-ce.interface';
import { IClaimsGroupClaimV2 } from '@module-claims/models/claims-group.interface';
import { ClaimsService } from '@module-claims/services/claims.service';
import { IAsyncData, IPendingState } from '@module-claims/store/claims.state';
import {
  Contact340BAuditClaimsCEsModalClose,
  Contact340BAuditClaimsCEsModalSave
} from '@module-claims/store/claim-ce.actions';
import { validatorPublicDomain } from '@validators-global/validator-public-domain';
import { validatorRegexp } from '@validators-global/validator-regexp';
import { VALIDATION_ERROR } from '@models-global/validation-errors';
import { validatorUserUniqueEmail } from '@module-user/validators/validator-user-unique-email';
import { UserService } from '@module-user/services/user/user.service';
import { getValidationMessage } from '@utils-global/validation-messages';

interface ClaimColumnConfiguration {
  title: string;
  field: string;
  type: string;
}

interface CESelection {
  intakeId: string;
  id340b: string;
}

@Component({
  selector: 'app-contact-ce-modal',
  templateUrl: './contact-ce-modal.component.html',
  styleUrls: ['./contact-ce-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ContactCeModalComponent
  extends DialogContentBase
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() claims!: IClaimsGroupClaimV2[];

  @ViewChild('claimsGrid') private claimsGrid!: GridComponent;

  readonly destroy$ = new Subject();

  auditClaimsCEs$: Observable<IAsyncData<ClaimsCEsGetResponse>>;
  auditClaimsCEsContact$: Observable<IPendingState>;
  emailValidationLoading$ = new Map<string, Subject<boolean>>();

  nameValidatorFns = new Map<string, ValidatorFn[]>();
  emailValidatorFns = new Map<string, ValidatorFn[]>();
  emailAsyncValidatorFns = new Map<string, AsyncValidatorFn[]>();

  claimsColumns = new Map<ClaimColumn, ClaimColumnConfiguration>();
  selectedCEs: CESelection[] = [];

  form!: UntypedFormGroup;

  constructor(
    private _dialogRef: DialogRef,
    private _store: Store,
    private _fb: UntypedFormBuilder,
    private _claimsService: ClaimsService,
    private _cdr: ChangeDetectorRef,
    private _userHttpService: UserService
  ) {
    super(_dialogRef);
    this.auditClaimsCEs$ = this._claimsService.auditClaimsCEs$;
    this.auditClaimsCEsContact$ = this._claimsService.auditClaimsCEsContact$;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.initializeClaimColumns();
    this._claimsService.clear340BAuditClaimsCEs();

    if (this.claims.length) {
      this._claimsService.get340BAuditClaimsCEs({
        intakeIds: this.claims.map((claim) => claim.IntakeId)
      });
    }

    this.auditClaimsCEs$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      (state.data?.claims ?? []).forEach((claim) => {
        claim.ceList.forEach((ce) => {
          if (!ce.contactName || !ce.contactEmail) {
            const nameCtrlName = `name_${ce.id340b}`;
            const emailCtrlName = `email_${ce.id340b}`;

            this.emailValidationLoading$.set(ce.id340b, new Subject<boolean>());

            this.nameValidatorFns.set(ce.id340b, [
              Validators.required,
              Validators.maxLength(100),
              validatorRegexp(/^[A-Za-z-, ]+$/, VALIDATION_ERROR.ONLY_LETTERS)
            ]);
            this.emailValidatorFns.set(ce.id340b, [
              Validators.required,
              Validators.maxLength(100),
              Validators.email,
              validatorPublicDomain(),
              this.validatorUniqueCEEmail(ce.id340b)
            ]);
            this.emailAsyncValidatorFns.set(ce.id340b, [
              validatorUserUniqueEmail(
                this._userHttpService,
                this._cdr,
                undefined,
                this.emailValidationLoading$.get(ce.id340b),
                true
              )
            ]);

            this.form.addControl(nameCtrlName, new UntypedFormControl());
            this.form.addControl(emailCtrlName, new UntypedFormControl());
          }
        });
      });
    });
  }

  ngAfterViewInit(): void {
    this.auditClaimsCEs$
      .pipe(
        filter((state) => !!state.data?.claims.length),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.expandAllRows(this.claimsGrid, this.claims.length);
        this._cdr.detectChanges();
      });
  }

  expandAllRows(grid: GridComponent, length: number): void {
    for (let i = 0; i < length; i++) {
      grid.expandRow(i);
    }
  }

  getClaimCEs(
    claim: IClaimsGroupClaimV2,
    claimsCEs: IAsyncData<ClaimsCEsGetResponse>
  ): ClaimCEContact[] {
    return (
      (claimsCEs?.data?.claims ?? []).find((c) => c.intakeId === claim.IntakeId)
        ?.ceList ?? []
    );
  }

  selectCE(claim: IClaimsGroupClaimV2, e: SelectionEvent): void {
    if (e.selectedRows?.length) {
      const selectedItems: CESelection[] = e.selectedRows.map((row) => ({
        intakeId: claim.IntakeId,
        id340b: (row.dataItem as ClaimCEContact).id340b
      }));

      this.selectedCEs = this.selectedCEs.concat(selectedItems);

      selectedItems.forEach((i) => {
        const nameCtrl = this.form.get(`name_${i.id340b}`);
        nameCtrl?.addValidators(this.nameValidatorFns.get(i.id340b) ?? []);
        nameCtrl?.updateValueAndValidity();
        nameCtrl?.markAsDirty();

        const emailCtrl = this.form.get(`email_${i.id340b}`);
        emailCtrl?.addValidators(this.emailValidatorFns.get(i.id340b) ?? []);
        emailCtrl?.addAsyncValidators(
          this.emailAsyncValidatorFns.get(i.id340b) ?? []
        );
        emailCtrl?.updateValueAndValidity();
        emailCtrl?.markAsDirty();
      });
    } else if (e.deselectedRows?.length) {
      const deselectedItems: CESelection[] = e.deselectedRows.map((row) => ({
        intakeId: claim.IntakeId,
        id340b: (row.dataItem as ClaimCEContact).id340b
      }));

      this.selectedCEs = this.selectedCEs.filter(
        (ce) =>
          !deselectedItems.find(
            (i) => i.intakeId === ce.intakeId && i.id340b === ce.id340b
          )
      );

      // Remove validators only if CE is not selected under another claim
      deselectedItems
        .filter((i) => !this.selectedCEs.some((ce) => ce.id340b === i.id340b))
        .forEach((i) => {
          const nameCtrl = this.form.get(`name_${i.id340b}`);
          nameCtrl?.removeValidators(this.nameValidatorFns.get(i.id340b) ?? []);
          nameCtrl?.updateValueAndValidity();
          nameCtrl?.markAsDirty();

          const emailCtrl = this.form.get(`email_${i.id340b}`);
          emailCtrl?.removeValidators(
            this.emailValidatorFns.get(i.id340b) ?? []
          );
          emailCtrl?.removeAsyncValidators(
            this.emailAsyncValidatorFns.get(i.id340b) ?? []
          );
          emailCtrl?.updateValueAndValidity();
          emailCtrl?.markAsDirty();
        });
    } else {
      this.selectedCEs = this.selectedCEs.filter(
        (ce) => ce.intakeId !== claim.IntakeId
      );
    }
  }

  isCERowSelectedFn = (claim: IClaimsGroupClaimV2): RowSelectedFn => {
    return (e: RowArgs): boolean => {
      const rowCE: ClaimCEContact = e.dataItem;
      return this.selectedCEs.some(
        (ce) => ce.intakeId === claim.IntakeId && ce.id340b === rowCE.id340b
      );
    };
  };

  isCERowSelected = (
    claim: IClaimsGroupClaimV2,
    ceContact: ClaimCEContact
  ): boolean => {
    return this.selectedCEs.some(
      (ce) => ce.intakeId === claim.IntakeId && ce.id340b === ceContact.id340b
    );
  };

  getControlErrorMsg(controlName: string): string {
    const control = this.form.get(controlName);
    if (control) {
      return getValidationMessage(control);
    }
    return '';
  }

  close(): void {
    this._store.dispatch(new Contact340BAuditClaimsCEsModalClose());
  }

  submit(): void {
    this.form.statusChanges
      .pipe(
        startWith(this.form.status),
        filter((status) => status !== 'PENDING'),
        take(1)
      )
      .subscribe(() => {
        this.contactCEs();
      });
  }

  contactCEs(): void {
    if (!this.selectedCEs.length || this.form.invalid) {
      for (const key of Object.keys(this.form.controls)) {
        this.form.get(key)?.markAsDirty();
      }
      return;
    }

    const ceContactParams: ClaimsCEsContactParams = this.selectedCEs.reduce(
      (acc: ClaimsCEsContactParams, selectedCE: CESelection) => {
        const { intakeId, id340b } = selectedCE;
        const ce = acc.ceList.find((ce) => ce.id340b);
        if (ce && !ce.intakeIds.includes(intakeId)) {
          ce.intakeIds = ce.intakeIds.concat(intakeId);
        } else {
          const nameCtrl = this.form.get(`name_${id340b}`);
          const emailCtrl = this.form.get(`email_${id340b}`);
          acc.ceList = acc.ceList.concat({
            id340b,
            intakeIds: [intakeId],
            contactData:
              nameCtrl && emailCtrl
                ? {
                    name: ((nameCtrl.value as string) ?? '').trim(),
                    email: ((emailCtrl.value as string) ?? '')
                      .trim()
                      .toLowerCase()
                  }
                : undefined
          });
        }
        return acc;
      },
      { ceList: [] }
    );

    this._store.dispatch(
      new Contact340BAuditClaimsCEsModalSave(ceContactParams)
    );
  }

  private initializeForm(): void {
    this.form = this._fb.group([]);
  }

  private initializeClaimColumns(): void {
    const createColumn = (
      title: string,
      field: keyof IClaimsGroupClaimV2,
      opts?: {
        type?: 'string' | 'numeric' | 'percentage' | 'date' | 'dropdown';
      }
    ): ClaimColumnConfiguration => ({
      title,
      field,
      type: opts?.type ?? 'string'
    });

    const columns: [ClaimColumn, ClaimColumnConfiguration][] = [
      [ClaimColumn.LINE_OF_BUSINESS, createColumn('LOB', 'lobName')],
      [ClaimColumn.CLAIM_ID, createColumn('Claim ID', 'ClaimId')],
      [ClaimColumn.CLAIM_TYPE, createColumn('Claim Type', 'ClaimType')],
      [ClaimColumn.PLAN_ID, createColumn('Plan ID', 'PlanId')],
      [ClaimColumn.PLAN_NAME, createColumn('Plan Name', 'PlanName')],
      [
        ClaimColumn.DATE_OF_SERVICE,
        createColumn('Date Filled', 'FillDate', { type: 'date' })
      ],
      [ClaimColumn.DRUG_NAME, createColumn('Drug Name', 'DrugName')],
      [ClaimColumn.NDC, createColumn('NDC', 'ndc')],
      [
        ClaimColumn.QUANTITY,
        createColumn('Quantity', 'FinalQuantity', { type: 'numeric' })
      ],
      [
        ClaimColumn.REBATE_AMOUNT,
        createColumn('Rebate Amount', 'RebateAmount', { type: 'numeric' })
      ],
      [ClaimColumn.PRESCRIBER_NAME, createColumn('Prescriber', 'ProviderName')],
      [ClaimColumn.PHARMACY_NAME, createColumn('Pharmacy', 'PharmacyName')],
      [
        ClaimColumn.STATUS_340B,
        createColumn('340B Status', 'StatusCategory340B', {
          type: 'dropdown'
        })
      ],
      [
        ClaimColumn.STATUS_DISPUTED,
        createColumn('Dispute Status', 'isDisputed', {
          type: 'dropdown'
        })
      ],
      [
        ClaimColumn.STATUS_APPROVED,
        createColumn('Resolution Status', 'isApproved', {
          type: 'dropdown'
        })
      ],
      [
        ClaimColumn.ENTITY_TYPE_340B,
        createColumn('340B Entity Type', 'entityType340b')
      ],
      [ClaimColumn.CE_ID_340B, createColumn('340B CE', 'CEID340B')],
      [ClaimColumn.REASON_340B, createColumn('340B Reasoning', 'ReasonText')]
    ];

    this.claimsColumns = new Map(columns as any);
  }

  private validatorUniqueCEEmail(id340b: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const val = control.value?.trim()?.toLowerCase();

      if (!val) return null;

      const isNotUnique = this.selectedCEs.some(
        (ce) =>
          ce.id340b !== id340b &&
          this.form.get(`email_${ce.id340b}`)?.value?.trim()?.toLowerCase() ===
            val
      );

      return isNotUnique ? { [VALIDATION_ERROR.UNIQUE_EMAIL]: true } : null;
    };
  }
}
